import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ChannelBucket, ChannelScope } from '../api/types';

export interface ChannelChartProps {
  byChannel: ChannelBucket[] | null;
  channelScope: ChannelScope | null;
}

const CHANNEL_LABELS: Record<string, string> = {
  contact_sync: 'Contact sync',
  share_link: 'Share link',
  sms: 'SMS',
  email: 'Email',
  qr_code: 'QR code',
  social: 'Social',
};

/** A named channel gets its display label; `null` is "not recorded" — a signup
 *  referral whose client sent no channel, never merged with an unknown string. */
function channelLabel(channel: string | null): string {
  if (channel === null) return 'Not recorded';
  return CHANNEL_LABELS[channel] ?? channel;
}

function fmtNum(v: number): string {
  return v.toLocaleString();
}

interface Row {
  key: string;
  label: string;
  counted: number;
  verified: number;
  isUnrecorded: boolean;
}

interface ChannelTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: Row }>;
}

function ChannelTooltip({ active, payload }: ChannelTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl">
      <div className="text-white font-semibold tabular-nums">{fmtNum(p.counted)} referrals</div>
      <div className="text-gray-400 mt-0.5">
        {fmtNum(p.verified)} verified &middot; {p.label}
      </div>
    </div>
  );
}

function scopeCaption(channelScope: ChannelScope | null): string {
  if (channelScope === null) {
    return 'Signup referrals only — the settings-referral count is not available.';
  }
  if (channelScope.settings_entered > 0) {
    const n = channelScope.settings_entered;
    return `Signup referrals only — ${fmtNum(n)} settings referral${n === 1 ? '' : 's'} excluded, that path records no channel.`;
  }
  return 'Signup referrals only.';
}

/**
 * `by_channel` covers SIGNUP-entered referrals only: `invite_channel` is
 * always null for settings redemptions (migration V159), so those rows are
 * excluded from this breakdown entirely rather than folded into a bucket.
 * The caption states that scope from `channel_scope` — without it, a missing
 * `contact_sync` bar reads as "no contact invites happened", which is false.
 *
 * A `channel: null` bucket is different again: it IS a signup referral, just
 * one whose client sent no channel. It is rendered as "Not recorded" in a
 * neutral gray, visibly distinct from the named (violet) channels, so it is
 * never mistaken for the excluded settings referrals.
 */
export function ChannelChart({ byChannel, channelScope }: ChannelChartProps): React.ReactElement {
  if (byChannel === null) {
    return (
      <div className="h-56 flex items-center justify-center text-sm text-gray-500">
        Not available
      </div>
    );
  }

  const rows: Row[] = byChannel
    .map((b) => ({
      key: b.channel ?? '__unrecorded__',
      label: channelLabel(b.channel),
      counted: b.counted,
      verified: b.verified,
      isUnrecorded: b.channel === null,
    }))
    // Named channels first, by volume; "Not recorded" always last so it never
    // gets lost among — or mistaken for — a real channel.
    .sort((a, b) => {
      if (a.isUnrecorded !== b.isUnrecorded) return a.isUnrecorded ? 1 : -1;
      return b.counted - a.counted;
    });

  return (
    <div>
      <p className="mb-3 text-[11px] text-gray-500">{scopeCaption(channelScope)}</p>
      {rows.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-sm text-gray-500">
          No signup referrals in this period.
        </div>
      ) : (
        <div className="h-48 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 8, right: 32, left: 8, bottom: 0 }}
              barCategoryGap="30%"
            >
              <CartesianGrid stroke="#1f2937" horizontal={false} />
              <XAxis type="number" allowDecimals={false} stroke="#6b7280" fontSize={11} />
              <YAxis
                type="category"
                dataKey="label"
                stroke="#6b7280"
                fontSize={11}
                width={100}
                tickLine={false}
              />
              <Tooltip content={<ChannelTooltip />} cursor={{ fill: '#a78bfa', fillOpacity: 0.08 }} />
              <Bar dataKey="counted" radius={[0, 4, 4, 0]} maxBarSize={24} isAnimationActive={false}>
                {rows.map((r) => (
                  <Cell key={r.key} fill={r.isUnrecorded ? '#6b7280' : '#a78bfa'} />
                ))}
                <LabelList
                  dataKey="counted"
                  position="right"
                  fill="#e5e7eb"
                  fontSize={11}
                  formatter={(v: string | number | boolean | null | undefined) =>
                    typeof v === 'number' ? v.toLocaleString() : v
                  }
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default ChannelChart;
